/**
 * O que aconteceu DENTRO da oportunidade, além das etapas (23/10/2026): nota,
 * documento, consulta e edição dos dados, cada um com quem fez e quando.
 *
 * ── Rota própria, e não um campo novo do histórico ───────────────────────
 *
 * O cliente da web valida a resposta contra o schema estrito da rota: um
 * campo novo em `GET .../historico` derrubaria a aba de quem estivesse com o
 * CRM aberto no deploy, até recarregar. A rota nova, a tela antiga nunca chama.
 *
 * ── Quem e quando vêm da auditoria; o conteúdo, de onde mora ─────────────
 *
 * O trecho da nota é o texto de hoje da nota, e o nome do documento é o nome
 * de hoje do documento: apagados (inclusive pela eliminação do titular), somem
 * daqui também. O título editado não traz os valores, que costumam ter o nome
 * do cliente. A consulta diz O QUE mudou, sem médico nem horário, que são dado
 * de saúde e ficam na aba Consulta.
 */

import { z } from 'zod';
import { InstanteSchema } from './valores.js';

const AtividadeBase = {
  quando: InstanteSchema,
  /** O nome de quem fez. Nulo quando não foi uma pessoa da equipe. */
  por: z.string().nullable(),
};

/** O valor no mesmo formato do funil: micros e moeda. */
const ValorNaEdicaoSchema = z
  .object({ quantia_micros: z.number().int(), moeda: z.string() })
  .strict();

export const MudancaNaOportunidadeSchema = z.discriminatedUnion('campo', [
  /** O título mudou; os valores ficam de fora porque costumam trazer o nome do cliente. */
  z.object({ campo: z.literal('titulo') }).strict(),
  z
    .object({
      campo: z.literal('valor'),
      antes: ValorNaEdicaoSchema.nullable(),
      depois: ValorNaEdicaoSchema.nullable(),
    })
    .strict(),
  z
    .object({
      campo: z.literal('fechamento_previsto'),
      antes: InstanteSchema.nullable(),
      depois: InstanteSchema.nullable(),
    })
    .strict(),
  /** O cliente trocou; quem era e quem é ficam no cadastro, e não aqui. */
  z.object({ campo: z.literal('cliente') }).strict(),
]);

export type MudancaNaOportunidade = z.infer<typeof MudancaNaOportunidadeSchema>;

export const AtividadeDaOportunidadeSchema = z.discriminatedUnion('tipo', [
  z
    .object({
      ...AtividadeBase,
      tipo: z.literal('nota'),
      acao: z.enum(['escrita', 'corrigida']),
      /** O começo do texto de hoje da nota. Nulo se ela não existe mais. */
      trecho: z.string().nullable(),
    })
    .strict(),
  z
    .object({
      ...AtividadeBase,
      tipo: z.literal('documento'),
      acao: z.enum(['enviado', 'removido']),
      /** O nome de hoje do documento. Nulo se ele não existe mais. */
      nome: z.string().nullable(),
    })
    .strict(),
  z
    .object({
      ...AtividadeBase,
      tipo: z.literal('consulta'),
      /**
       * `marcada`: agendada no SISAC. `escolhida`: médico, especialidade ou
       * horário mudou. `cancelada`: o agendamento foi cancelado no SISAC.
       */
      acao: z.enum(['marcada', 'escolhida', 'cancelada']),
      campos: z.array(z.enum(['medico', 'especialidade', 'horario'])),
    })
    .strict(),
  z
    .object({
      ...AtividadeBase,
      tipo: z.literal('edicao'),
      mudancas: z.array(MudancaNaOportunidadeSchema).min(1),
    })
    .strict(),
]);

export type AtividadeDaOportunidade = z.infer<typeof AtividadeDaOportunidadeSchema>;

export const AtividadesDaOportunidadeSchema = z
  .object({
    /** Do mais recente ao mais antigo, que é a ordem em que se lê. */
    itens: z.array(AtividadeDaOportunidadeSchema),
  })
  .strict();

export type AtividadesDaOportunidade = z.infer<typeof AtividadesDaOportunidadeSchema>;
